import React, { useState, useEffect } from 'react';
import { locationService, Location } from '@/firebase/locationService';
import { useNotification } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import {
  createInvitation,
  getAllInvitations,
  cancelInvitation,
  resendInvitation,
  completeStaffInvitationAsAdmin,
  Invitation,
} from '@/services/invitationService';
import { Plus, Trash2, X, MapPin, Edit, Clock, CheckCircle, AlertCircle, Building2, CheckSquare, Square, HelpCircle, UserMinus, History, Mail, Link2, UserPlus, KeyRound } from 'lucide-react';
import AssignLocationsPanel from './AssignLocationsPanel';
import RoleHelpModal from './RoleHelpModal';
import UserAuditTrail from './UserAuditTrail';
import { OWNER_ROLES, SYSTEM_ROLES, normalizeRoles, hasRole } from '@/constants/roles';
import { getOwnerRolesForOrganizationType } from '@/constants/organizationRoles';
import { 
  isAdminInOrganization, 
  isManagerInOrganization,
  getUserSystemRoleForOrganization,
  getUserOwnerRolesForOrganization
} from '@/utils/roleHelpers';
import { userService } from '@/firebase/database';
import { auditLogService } from '@/services/auditLogService';
import { getIndustryType } from '@/firebase/organizationService';
import { isEnterprisePlan } from '@/utils/enterpriseSecurity';
import { formatLoginIdentifierForDisplay, isSyntheticStaffAuthEmail, normalizeInviteIdentifier } from '@/utils/staffUsernameAuth';
import { getAcceptInvitationUrl } from '@/utils/domainUtils';
import { setOrganizationMemberPassword } from '@/services/organizationMemberAdminService';
import CustomRoleManagement from './CustomRoleManagement';
import CustomRoleAssignment from './CustomRoleAssignment';

// Helper function to check if current user can remove/delete target user based on roles
// Updated to use organization-specific roles
const canRemoveUser = (
  currentUser: any, 
  targetUser: any, 
  organizationId: string | undefined,
  isCurrentUser: boolean
): boolean => {
  // No one can remove themselves
  if (isCurrentUser) {
    return false;
  }

  // Need organizationId to check roles
  if (!organizationId) {
    return false;
  }

  const currentIsAdmin = isAdminInOrganization(currentUser, organizationId);
  const currentIsManager = isManagerInOrganization(currentUser, organizationId);
  const currentIsUser = !currentIsAdmin && !currentIsManager;

  const targetIsAdmin = isAdminInOrganization(targetUser, organizationId);
  const targetIsManager = isManagerInOrganization(targetUser, organizationId);
  const targetIsUser = !targetIsAdmin && !targetIsManager;

  // Admin can remove: Admin, Manager, User
  if (currentIsAdmin) {
    return true;
  }

  // Manager can remove: User only (not Admin or Manager)
  if (currentIsManager) {
    return targetIsUser; // Only regular users
  }

  // User cannot remove anyone
  if (currentIsUser) {
    return false;
  }

  // Default: cannot remove
  return false;
};

interface User {
  id: string;
  name: string;
  email: string;
  picture?: string;
  role?: string | string[]; // Support both single role (backward compatible) and multiple roles
  roles?: string[]; // Explicit roles array field (from migration)
  locationIds?: string[]; // Array of location IDs
  organizationId?: string; // Multi-tenant support
  createdAt?: any;
  updatedAt?: any;
}

const UserManagement: React.FC = () => {
  const { showNotification } = useNotification();
  const { user: currentUser, updateUser } = useAuth();
  const { currentOrganization } = useOrganization();
  
  // Get organization-type-specific owner roles
  // Use getIndustryType helper for backward compatibility
  const organizationType = getIndustryType(currentOrganization);
  const availableOwnerRoles = getOwnerRolesForOrganizationType(organizationType);
  
  // Debug: Log organization type and available roles
  useEffect(() => {
    if (currentOrganization?.id) {
      console.log('[UserManagement] Organization Type Detection:', {
        organizationId: currentOrganization.id,
        organizationName: currentOrganization.name,
        industryType: currentOrganization.industryType,
        detectedOrganizationType: organizationType,
        availableOwnerRoles: availableOwnerRoles,
        rolesCount: availableOwnerRoles.length
      });
    }
  }, [currentOrganization, organizationType, availableOwnerRoles]);
  
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>('All Locations');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForLocations, setUserForLocations] = useState<User | null>(null);
  const [actualUserRole, setActualUserRole] = useState<string | undefined>(currentUser?.role);
  const [showRoleHelp, setShowRoleHelp] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [showCompleteStaffModal, setShowCompleteStaffModal] = useState(false);
  const [completeStaffInvitationRow, setCompleteStaffInvitationRow] = useState<Invitation | null>(null);
  const [completeStaffPassword, setCompleteStaffPassword] = useState('');
  const [completeStaffConfirm, setCompleteStaffConfirm] = useState('');
  const [completeStaffLoading, setCompleteStaffLoading] = useState(false);
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [setPasswordForUser, setSetPasswordForUser] = useState<User | null>(null);
  const [setPasswordNew, setSetPasswordNew] = useState('');
  const [setPasswordConfirm, setSetPasswordConfirm] = useState('');
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  const [selectedUserForAudit, setSelectedUserForAudit] = useState<User | null>(null);
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'custom-roles'>('users');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    roles: ['user'] as string[], // Changed to array for multiple roles
    selectedLocationIds: [] as string[], // Location IDs to assign to the invited user
  });

  const currentUserRole = actualUserRole || currentUser?.role;
  // Note: currentUserRoles is kept for backward compatibility in some places
  // but new code should use organization-specific helpers
  const currentUserRoles = normalizeRoles(currentUserRole);

  // Load current user's role from Firestore for the current organization
  // This reloads when organization changes to show the correct role per organization
  useEffect(() => {
    const loadCurrentUserRole = async () => {
      if (currentUser?.id && currentOrganization?.id) {
        try {
          const { db } = await import('@/firebase/config');
          const { doc, getDoc } = await import('firebase/firestore');
          const userRef = doc(db, 'users', currentUser.id);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const organizationId = currentOrganization.id;
            
            // Get organization-specific system role
            const systemRole = getUserSystemRoleForOrganization(userData, organizationId);
            
            // Only update if the role has changed
            if (systemRole && systemRole !== actualUserRole) {
              setActualUserRole(systemRole);
              // Update AuthContext with organization-specific role for backward compatibility
              updateUser({ role: systemRole });
            } else if (!systemRole) {
              // Fallback to legacy role if no organization-specific role
              const legacyRole = userData.role;
              if (legacyRole && legacyRole !== actualUserRole) {
                setActualUserRole(legacyRole);
                updateUser({ role: legacyRole });
              }
            }
          }
        } catch (error) {
          console.error('Error loading current user role:', error);
        }
      } else if (currentUser?.id && !currentOrganization?.id) {
        // No organization selected - fall back to legacy role
        const { db } = await import('@/firebase/config');
        const { doc, getDoc } = await import('firebase/firestore');
        const userRef = doc(db, 'users', currentUser.id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const firestoreRole = userData.role;
          if (firestoreRole !== actualUserRole) {
            setActualUserRole(firestoreRole);
          }
        }
      }
    };
    
    loadCurrentUserRole();
  }, [currentUser?.id, currentOrganization?.id, updateUser, actualUserRole]);

  useEffect(() => {
    loadUsers();
    loadInvitations();
    loadLocations();
  }, [currentOrganization?.id, currentOrganization?.name]);

  // Check if Enterprise plan
  useEffect(() => {
    const checkEnterprise = async () => {
      if (currentOrganization?.id) {
        const enterprise = await isEnterprisePlan(currentOrganization.id);
        setIsEnterprise(enterprise);
      } else {
        setIsEnterprise(false);
      }
    };
    checkEnterprise();
  }, [currentOrganization?.id]);

  const loadUsers = async () => {
    // Don't load users if no organization
    if (!currentOrganization?.id) {
      setUsers([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { db } = await import('@/firebase/config');
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      const usersRef = collection(db, 'users');
      const organizationId = currentOrganization.id;
      
      // Query 1: Users with organizationId matching current organization (legacy support)
      const orgIdQuery = query(usersRef, where('organizationId', '==', organizationId));
      
      // Query 2: Users with organizationIds array containing current organization
      const orgIdsQuery = query(usersRef, where('organizationIds', 'array-contains', organizationId));
      
      // Execute both queries in parallel
      const [orgIdSnapshot, orgIdsSnapshot] = await Promise.all([
        getDocs(orgIdQuery),
        getDocs(orgIdsQuery)
      ]);
      
      // Combine results and deduplicate by user ID
      const usersMap = new Map<string, User>();
      
      // Process users from organizationId query
      orgIdSnapshot.forEach((doc) => {
        const data = doc.data();
        const userData: User = { 
          id: doc.id, 
          name: data.name || '',
          email: data.email || '',
          picture: data.picture,
          role: data.role,
          roles: data.roles || (data.role ? (Array.isArray(data.role) ? data.role : [data.role]) : undefined),
          locationIds: data.locationIds,
          organizationId: data.organizationId,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        };
        
        // Add organizationIds and organizationRoles to userData for UI logic
        const userOrgIds = data.organizationIds || [];
        (userData as any).organizationIds = userOrgIds;
        (userData as any).organizationRoles = data.organizationRoles;
        
        usersMap.set(doc.id, userData);
      });
      
      // Process users from organizationIds array query (may include duplicates)
      orgIdsSnapshot.forEach((doc) => {
        // Skip if already added from first query
        if (usersMap.has(doc.id)) return;
        
        const data = doc.data();
        const userData: User = { 
          id: doc.id, 
          name: data.name || '',
          email: data.email || '',
          picture: data.picture,
          role: data.role,
          roles: data.roles || (data.role ? (Array.isArray(data.role) ? data.role : [data.role]) : undefined),
          locationIds: data.locationIds,
          organizationId: data.organizationId,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        };
        
        // Add organizationIds and organizationRoles to userData for UI logic
        const userOrgIds = data.organizationIds || [];
        (userData as any).organizationIds = userOrgIds;
        (userData as any).organizationRoles = data.organizationRoles;
        
        usersMap.set(doc.id, userData);
      });
      
      // Convert map to array
      const usersList = Array.from(usersMap.values());
      
      setUsers(usersList);
    } catch (error: any) {
      console.error('Error loading users:', error);
      showNotification(`Error loading users: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadLocations = async () => {
    // Don't load locations if no organization
    if (!currentOrganization?.id) {
      setLocations([]);
      return;
    }

    const result = await locationService.getAllLocations(currentOrganization.id);
    if (result.success && result.data) {
      setLocations(result.data);
    }
  };

  const loadInvitations = async () => {
    // Don't load invitations if no organization
    if (!currentOrganization?.id) {
      setInvitations([]);
      return;
    }

    try {
      const result = await getAllInvitations();
      if (result.success && result.invitations) {
        // Filter invitations by organization
        const orgInvitations = result.invitations.filter(inv => 
          inv.organizationId === currentOrganization.id
        );
        setInvitations(orgInvitations);
      }
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };

  const handleAddUserClick = () => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to add users.', 'error');
      navigate('/subscription');
      return;
    }
    setShowAddModal(true);
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to add users.', 'error');
      navigate('/subscription');
      return;
    }
    
    if (!formData.name || !formData.email) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    const inviteResolved = normalizeInviteIdentifier(formData.email);
    if (inviteResolved.error || !inviteResolved.authEmail) {
      showNotification(inviteResolved.error || 'Invalid email or username', 'error');
      return;
    }

    // Validate that at least one location is selected
    if (!formData.selectedLocationIds || formData.selectedLocationIds.length === 0) {
      showNotification('Please assign at least one location to the user', 'error');
      return;
    }

    if (!currentUser) {
      showNotification('You must be logged in to send invitations', 'error');
      return;
    }

    // Check actual role from Firestore (using organization-specific roles)
    const organizationId = currentOrganization?.id;
    if (!organizationId) {
      showNotification('No organization selected', 'error');
      return;
    }
    
    // Prevent managers (but not admins) from inviting users as admin
    const currentUserSystemRole = getUserSystemRoleForOrganization(currentUser, organizationId);
    if (currentUserSystemRole === 'manager' && formData.roles.includes('admin')) {
      showNotification('Managers cannot invite users as admin. Only admins can create admin users.', 'error');
      return;
    }

    // Ensure exactly one system role is selected
    const systemRoles = formData.roles.filter(r => SYSTEM_ROLES.includes(r as any));
    if (systemRoles.length === 0) {
      showNotification('Please select one system role (User, Admin, or Manager)', 'error');
      return;
    }
    if (systemRoles.length > 1) {
      showNotification('Please select only one system role (User, Admin, or Manager)', 'error');
      return;
    }

    setIsLoading(true);
    try {
      // For invitations, use the system role for backward compatibility
      // The actual roles array will be set when user accepts invitation
      // Note: Only one system role is allowed, validation ensures this above
      const primaryRole = systemRoles[0] || 'user';
      
      const result = await createInvitation({
        email: inviteResolved.authEmail,
        name: formData.name,
        role: primaryRole, // Store primary role for backward compatibility
        roles: formData.roles, // Store full roles array
        invitedBy: currentUser.id,
        organizationId: currentOrganization?.id,
        locationIds: formData.selectedLocationIds // Include selected location IDs
      });
      
      if (result.success) {
        // Track user invitation
        const { trackOrganization } = await import('@/utils/analytics');
        trackOrganization.userInvited(currentOrganization?.id || '', primaryRole);

        if (isSyntheticStaffAuthEmail(inviteResolved.authEmail) && result.inviteToken) {
          const url = getAcceptInvitationUrl(result.inviteToken);
          try {
            await navigator.clipboard.writeText(url);
            showNotification(
              'Invitation created. The invite link was copied — share it with this person (no email is sent for staff usernames).',
              'success'
            );
          } catch {
            showNotification(
              `Staff usernames do not receive email. Share this link with them: ${url}`,
              'success'
            );
          }
        } else {
          showNotification(
            'Invitation sent successfully! The user will receive an email to set up their account.',
            'success'
          );
        }
        setShowAddModal(false);
        setFormData({ name: '', email: '', roles: ['user'], selectedLocationIds: [] });
        await loadInvitations();
      } else {
        showNotification(result.error || 'Failed to send invitation', 'error');
      }
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      showNotification(`Error sending invitation: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const copyInvitationLink = async (token: string) => {
    const url = getAcceptInvitationUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      showNotification('Invite link copied to clipboard', 'success');
    } catch {
      showNotification(`Copy this URL manually: ${url}`, 'info');
    }
  };

  const handleSubmitCompleteStaffInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!completeStaffInvitationRow) return;
    if (completeStaffPassword && completeStaffPassword !== completeStaffConfirm) {
      showNotification('Passwords do not match', 'error');
      return;
    }
    if (completeStaffPassword && completeStaffPassword.length < 6) {
      showNotification('Password must be at least 6 characters, or leave both fields blank', 'error');
      return;
    }

    setCompleteStaffLoading(true);
    try {
      const result = await completeStaffInvitationAsAdmin(
        completeStaffInvitationRow.id,
        completeStaffPassword.trim() || undefined
      );
      if (!result.success) {
        showNotification(result.error || 'Could not complete invitation', 'error');
        return;
      }
      if (result.existingAccount) {
        showNotification(
          'This person already had an account. They now have access to your organization.',
          'success'
        );
      } else if (result.initialPassword) {
        try {
          await navigator.clipboard.writeText(result.initialPassword);
          showNotification(
            `Account created. Temporary password copied to clipboard. Share it with them securely, then ask them to sign in and change it.`,
            'success'
          );
        } catch {
          showNotification(
            `Account created. Temporary password: ${result.initialPassword}. Share it securely with them.`,
            'success'
          );
        }
      } else {
        showNotification('Account created. Share the username and password you set with them.', 'success');
      }
      setShowCompleteStaffModal(false);
      setCompleteStaffInvitationRow(null);
      setCompleteStaffPassword('');
      setCompleteStaffConfirm('');
      await loadInvitations();
      await loadUsers();
    } catch (err: any) {
      showNotification(err?.message || 'Failed to complete invitation', 'error');
    } finally {
      setCompleteStaffLoading(false);
    }
  };

  const canSetStaffMemberPassword = (user: User): boolean => {
    const organizationId = currentOrganization?.id;
    if (!organizationId || !currentUser) return false;
    if (!isSyntheticStaffAuthEmail(user.email)) return false;
    const callerRole = getUserSystemRoleForOrganization(currentUser, organizationId);
    if (callerRole !== 'admin' && callerRole !== 'manager') return false;
    const targetRole = getUserSystemRoleForOrganization(user, organizationId);
    if (callerRole === 'manager' && targetRole === 'admin') return false;
    return true;
  };

  const handleSubmitSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const organizationId = currentOrganization?.id;
    if (!organizationId || !setPasswordForUser?.id) return;
    if (setPasswordNew.length < 6) {
      showNotification('Password must be at least 6 characters', 'error');
      return;
    }
    if (setPasswordNew !== setPasswordConfirm) {
      showNotification('Passwords do not match', 'error');
      return;
    }
    setSetPasswordLoading(true);
    try {
      const result = await setOrganizationMemberPassword(organizationId, setPasswordForUser.id, setPasswordNew);
      if (!result.success) {
        showNotification(result.error || 'Could not set password', 'error');
        return;
      }
      showNotification('New password saved. Share it with the team member securely.', 'success');
      setShowSetPasswordModal(false);
      setSetPasswordForUser(null);
      setSetPasswordNew('');
      setSetPasswordConfirm('');
    } finally {
      setSetPasswordLoading(false);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    if (!confirm('Resend invitation email to this user?')) {
      return;
    }
    
    try {
      const result = await resendInvitation(invitationId);
      if (result.success) {
        if (result.warning) {
          showNotification(result.warning, 'info');
        } else {
          showNotification('Invitation email resent successfully', 'success');
        }
        await loadInvitations();
      } else {
        showNotification(result.error || 'Failed to resend invitation', 'error');
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      showNotification('Failed to resend invitation', 'error');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      const result = await cancelInvitation(invitationId);
      if (result.success) {
        showNotification('Invitation cancelled successfully', 'success');
        await loadInvitations();
      } else {
        showNotification(result.error || 'Failed to cancel invitation', 'error');
      }
    } catch (error: any) {
      console.error('Error cancelling invitation:', error);
      showNotification(`Error cancelling invitation: ${error.message}`, 'error');
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to edit users.', 'error');
      navigate('/subscription');
      return;
    }
    
    if (!formData.name || !formData.email || !editingUser) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    const emailResolved = normalizeInviteIdentifier(formData.email);
    if (emailResolved.error || !emailResolved.authEmail) {
      showNotification(emailResolved.error || 'Invalid email or username', 'error');
      return;
    }

    const organizationId = currentOrganization?.id;
    if (!organizationId) {
      showNotification('No organization selected', 'error');
      return;
    }

    const editingUserSystemRole = getUserSystemRoleForOrganization(editingUser, organizationId);
    const hasSystemAdmin = editingUserSystemRole === 'admin';
    const currentUserSystemRole = getUserSystemRoleForOrganization(currentUser, organizationId);
    const currentUserIsManager = currentUserSystemRole === 'manager'; // Only true for managers, not admins
    const currentUserIsAdmin = currentUserSystemRole === 'admin';
    
    // Role-based permissions: System Admin can only be edited by themselves
    if (hasSystemAdmin && currentUser?.id !== editingUser.id && !currentUserIsAdmin) {
      showNotification('You cannot edit admin users. Only admins can edit themselves.', 'error');
      return;
    }
    
    // Users with only 'user' role cannot edit themselves
    if (editingUserSystemRole === 'user' && currentUser?.id === editingUser.id) {
      showNotification('You do not have permission to edit your own account.', 'error');
      return;
    }
    
    // Prevent managers (not admins) from assigning admin role
    if (currentUserIsManager && formData.roles.includes('admin')) {
      showNotification('Managers cannot assign admin role to users.', 'error');
      return;
    }
    
    // Prevent admin users from removing their own admin role
    if (hasSystemAdmin && !formData.roles.includes('admin') && currentUser?.id === editingUser.id) {
      showNotification('Admin users cannot remove their own admin role.', 'error');
      return;
    }

    // Ensure exactly one system role is selected
    const systemRoles = formData.roles.filter(r => SYSTEM_ROLES.includes(r as any));
    if (systemRoles.length === 0) {
      showNotification('Please select one system role (User, Admin, or Manager)', 'error');
      return;
    }
    if (systemRoles.length > 1) {
      showNotification('Please select only one system role (User, Admin, or Manager)', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const { db } = await import('@/firebase/config');
      const { doc, updateDoc, getDoc, serverTimestamp } = await import('firebase/firestore');
      
      const userRef = doc(db, 'users', editingUser.id);
      
      // Get old values for audit log
      const userSnap = await getDoc(userRef);
      const oldData = userSnap.exists() ? userSnap.data() : {};
      const oldRoles = normalizeRoles(oldData.roles || oldData.role);
      
      // Store roles as array, but also keep primary role for backward compatibility
      // Note: Only one system role is allowed, validation ensures this above
      const primaryRole = systemRoles[0] || 'user';
      
      // Filter owner roles (exclude system roles)
      // Use availableOwnerRoles (organization-type-specific) instead of global OWNER_ROLES
      // This ensures bank-specific roles like "Loan Officer" are included
      const ownerRoles = formData.roles.filter(r => 
        !SYSTEM_ROLES.includes(r as any) && availableOwnerRoles.includes(r)
      );
      
      // Ensure we're only saving one system role (backward compatibility safeguard)
      const finalRoles = [primaryRole, ...ownerRoles];
      
      // Build or update organizationRoles structure
      let organizationRoles: { [key: string]: { systemRole: string; ownerRoles: string[] } } = {};
      
      // Get existing organizationRoles if any
      if (oldData.organizationRoles && typeof oldData.organizationRoles === 'object') {
        organizationRoles = { ...oldData.organizationRoles };
      }
      
      // Update roles for the current organization
      organizationRoles[organizationId] = {
        systemRole: primaryRole,  // SINGLE VALUE
        ownerRoles: ownerRoles     // ARRAY
      };
      
      // Prepare update data
      const updateData: any = {
        name: formData.name,
        email: emailResolved.authEmail,
        role: primaryRole, // Keep for backward compatibility
        roles: finalRoles, // Keep for backward compatibility
        organizationRoles: organizationRoles, // NEW: Organization-specific roles
        updatedAt: serverTimestamp(),
      };
      
      await updateDoc(userRef, updateData);
      
      // Log audit entry for changes
      if (currentUser?.id && currentOrganization?.id) {
        const changes = [];
        
        // Check for role changes (organization-specific)
        const oldOrgRoles = oldData.organizationRoles?.[organizationId];
        const oldSystemRole = oldOrgRoles?.systemRole || oldData.role;
        // Use availableOwnerRoles for backward compatibility check too
        const oldOwnerRoles = oldOrgRoles?.ownerRoles || (oldData.roles ? oldData.roles.filter((r: string) => !SYSTEM_ROLES.includes(r as any) && availableOwnerRoles.includes(r)) : []);
        const newSystemRole = primaryRole;
        const newOwnerRoles = ownerRoles;
        
        const systemRoleChanged = oldSystemRole !== newSystemRole;
        const ownerRolesChanged = JSON.stringify(oldOwnerRoles.sort()) !== JSON.stringify(newOwnerRoles.sort());
        const rolesChanged = systemRoleChanged || ownerRolesChanged;
        
        if (rolesChanged) {
          changes.push({
            field: 'organizationRoles',
            organizationId: organizationId,
            oldValue: {
              systemRole: oldSystemRole,
              ownerRoles: oldOwnerRoles
            },
            newValue: {
              systemRole: newSystemRole,
              ownerRoles: newOwnerRoles
            },
          });
          
          // Also log legacy roles change for backward compatibility
          const oldRoles = normalizeRoles(oldData.roles || oldData.role);
          if (JSON.stringify(oldRoles.sort()) !== JSON.stringify(finalRoles.sort())) {
            changes.push({
              field: 'roles',
              oldValue: oldRoles,
              newValue: finalRoles,
            });
          }
        }
        
        // Check for name changes
        if (oldData.name !== formData.name) {
          changes.push({
            field: 'name',
            oldValue: oldData.name,
            newValue: formData.name,
          });
        }
        
        // Check for email changes
        if (oldData.email !== emailResolved.authEmail) {
          changes.push({
            field: 'email',
            oldValue: oldData.email,
            newValue: emailResolved.authEmail,
          });
        }
        
        if (changes.length > 0) {
          await auditLogService.log({
            userId: editingUser.id,
            action: rolesChanged ? 'role_changed' : 'user_updated',
            performedBy: currentUser.id,
            performedByName: currentUser.name || 'Unknown',
            performedByEmail: currentUser.email || 'Unknown',
            organizationId: currentOrganization.id,
            changes,
          });
        }
      }
      
      showNotification('User updated successfully', 'success');
      setShowEditModal(false);
      setEditingUser(null);
      setFormData({ name: '', email: '', roles: ['user'], selectedLocationIds: [] });
      await loadUsers();
    } catch (error: any) {
      console.error('Error updating user:', error);
      showNotification(`Error updating user: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEditModal = (user: User) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to edit users.', 'error');
      navigate('/subscription');
      return;
    }

    const organizationId = currentOrganization?.id;
    if (!organizationId) {
      showNotification('No organization selected', 'error');
      return;
    }

    // Get organization-specific roles
    const userSystemRole = getUserSystemRoleForOrganization(user, organizationId);
    const userOwnerRoles = getUserOwnerRolesForOrganization(user, organizationId);
    const hasSystemAdmin = userSystemRole === 'admin';
    
    // Role-based permissions: System Admin can only be edited by themselves
    if (hasSystemAdmin && currentUser?.id !== user.id) {
      showNotification('You cannot edit admin users. Only admins can edit themselves.', 'error');
      return;
    }
    
    // Users with only 'user' role cannot edit themselves
    if (userSystemRole === 'user' && currentUser?.id === user.id) {
      showNotification('You do not have permission to edit your own account.', 'error');
      return;
    }
    
    setEditingUser(user);
    
    // Prepare form data with organization-specific roles
    const selectedSystemRole = userSystemRole || 'user';
    // Ensure userOwnerRoles doesn't contain any system roles (safety check)
    const filteredOwnerRoles = userOwnerRoles.filter((role: string) => !SYSTEM_ROLES.includes(role as any));
    const finalRoles = [selectedSystemRole, ...filteredOwnerRoles];
    
    setFormData({
      name: user.name,
      email: formatLoginIdentifierForDisplay(user.email),
      roles: finalRoles,
      selectedLocationIds: [], // Not used in edit modal, but required for type consistency
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setFormData({ name: '', email: '', roles: ['user'], selectedLocationIds: [] });
  };

  const handleRemoveFromOrganization = async (userId: string) => {
    if (!currentOrganization?.id) {
      showNotification('No organization selected', 'error');
      return;
    }

    const userToRemove = users.find(u => u.id === userId);
    if (!userToRemove) {
      showNotification('User not found', 'error');
      return;
    }

    // Check permissions based on role hierarchy
    const organizationId = currentOrganization?.id;
    if (!organizationId) {
      showNotification('No organization selected', 'error');
      return;
    }
    
    const isCurrentUser = currentUser?.id === userId;

    if (!canRemoveUser(currentUser, userToRemove, organizationId, isCurrentUser)) {
      const targetIsAdmin = isAdminInOrganization(userToRemove, organizationId);
      const targetIsManager = isManagerInOrganization(userToRemove, organizationId);
      const currentIsManager = isManagerInOrganization(currentUser, organizationId);
      const currentIsUser = !isAdminInOrganization(currentUser, organizationId) && !currentIsManager;

      if (isCurrentUser) {
        showNotification('You cannot remove yourself from the organization.', 'error');
      } else if (targetIsAdmin && (currentIsManager || currentIsUser)) {
        showNotification('Only admins can remove admin users from the organization.', 'error');
      } else if (targetIsManager && (currentIsManager || currentIsUser)) {
        showNotification('Only admins can remove manager users from the organization.', 'error');
      } else {
        showNotification('You do not have permission to remove this user from the organization.', 'error');
      }
      return;
    }

    // Check if user belongs to multiple organizations
    const { db } = await import('@/firebase/config');
    const { doc, getDoc } = await import('firebase/firestore');
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      showNotification('User not found', 'error');
      return;
    }

    const userData = userSnap.data();
    const organizationIds = userData.organizationIds || [];
    const hasMultipleOrgs = organizationIds.length > 1 || 
                          (userData.organizationId && organizationIds.length > 0 && !organizationIds.includes(userData.organizationId));

    if (!hasMultipleOrgs && organizationIds.length === 1) {
      // User only belongs to this organization, ask if they want to delete instead
      if (!confirm('This user only belongs to this organization. Do you want to delete the user entirely instead of just removing them?')) {
        return;
      }
      // Fall through to delete user
      handleDeleteUser(userId);
      return;
    }

    if (!confirm(`Are you sure you want to remove ${userToRemove.name || userToRemove.email} from this organization? They will still have access to other organizations.`)) {
      return;
    }

    try {
      const result = await userService.removeUserFromOrganization(userId, currentOrganization.id);
      
      if (result.success) {
        // Log audit entry for user removal
        if (currentUser?.id) {
          await auditLogService.log({
            userId: userId,
            action: 'user_removed',
            performedBy: currentUser.id,
            performedByName: currentUser.name || 'Unknown',
            performedByEmail: currentUser.email || 'Unknown',
            organizationId: currentOrganization.id,
            metadata: {
              removedUserName: userToRemove.name,
              removedUserEmail: userToRemove.email,
            },
          });
        }
        
        showNotification('User removed from organization successfully', 'success');
        await loadUsers();
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error removing user from organization:', error);
      showNotification(`Error removing user from organization: ${error.message}`, 'error');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    // Find the user to check their role
    const userToDelete = users.find(u => u.id === userId);
    
    if (!userToDelete) {
      showNotification('User not found', 'error');
      return;
    }
    
    const organizationId = currentOrganization?.id;
    if (!organizationId) {
      showNotification('No organization selected', 'error');
      return;
    }

    const isCurrentUser = currentUser?.id === userId;

    // Check permissions based on role hierarchy (using organization-specific roles)
    if (!canRemoveUser(currentUser, userToDelete, organizationId, isCurrentUser)) {
      const targetIsAdmin = isAdminInOrganization(userToDelete, organizationId);
      const targetIsManager = isManagerInOrganization(userToDelete, organizationId);
      const currentIsManager = isManagerInOrganization(currentUser, organizationId);
      const currentIsUser = !isAdminInOrganization(currentUser, organizationId) && !currentIsManager;

      if (isCurrentUser) {
        showNotification('You cannot delete your own account.', 'error');
      } else if (targetIsAdmin && (currentIsManager || currentIsUser)) {
        showNotification('Only admins can delete admin users.', 'error');
      } else if (targetIsManager && (currentIsManager || currentIsUser)) {
        showNotification('Only admins can delete manager users.', 'error');
      } else {
        showNotification('You do not have permission to delete this user.', 'error');
      }
      return;
    }
    
    if (!confirm('Are you sure you want to delete this user? This will remove them from ALL organizations and cannot be undone.')) return;

    try {
      const { db } = await import('@/firebase/config');
      const { doc, deleteDoc } = await import('firebase/firestore');
      
      const userRef = doc(db, 'users', userId);
      await deleteDoc(userRef);
      
      showNotification('User deleted successfully', 'success');
      await loadUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      showNotification(`Error deleting user: ${error.message}`, 'error');
    }
  };

  const handleAssignLocations = (user: User) => {
    const organizationId = currentOrganization?.id;
    if (!organizationId) {
      showNotification('No organization selected', 'error');
      return;
    }
    
    const userSystemRole = getUserSystemRoleForOrganization(user, organizationId);
    const hasSystemAdmin = userSystemRole === 'admin';
    const currentUserHasSystemAdmin = isAdminInOrganization(currentUser, organizationId);
    
    if (hasSystemAdmin && !currentUserHasSystemAdmin) {
      showNotification('Only admins can assign locations to admin users.', 'error');
      return;
    }

    // Users with only 'user' role cannot assign locations to themselves
    if (userSystemRole === 'user' && currentUser?.id === user.id) {
      showNotification('You do not have permission to assign locations to your own account.', 'error');
      return;
    }
    
    setUserForLocations(user);
  };

  const handleSaveLocationAssignment = async (locationIds: string[]) => {
    if (!userForLocations?.id || !currentOrganization?.id) return;
    
    const organizationId = currentOrganization.id;
    const userSystemRole = getUserSystemRoleForOrganization(userForLocations, organizationId);
    const hasSystemAdmin = userSystemRole === 'admin';
    const currentUserHasSystemAdmin = isAdminInOrganization(currentUser, organizationId);
    
    if (hasSystemAdmin && !currentUserHasSystemAdmin) {
      showNotification('Only admins can assign locations to admin users.', 'error');
      return;
    }
    
    // Users with only 'user' role cannot assign locations to themselves
    if (userSystemRole === 'user' && currentUser?.id === userForLocations.id) {
      showNotification('You do not have permission to assign locations to your own account.', 'error');
      return;
    }

    try {
      const { db } = await import('@/firebase/config');
      const { doc, updateDoc, getDoc, serverTimestamp } = await import('firebase/firestore');
      
      const userRef = doc(db, 'users', userForLocations.id);
      
      // Get old locationIds for audit log
      const userSnap = await getDoc(userRef);
      const oldLocationIds = userSnap.exists() ? (userSnap.data().locationIds || []) : [];
      
      // Get all locations to determine which belong to current organization
      // Create a map of locationId -> organizationId for quick lookup
      const locationOrgMap = new Map<string, string>();
      locations.forEach(loc => {
        if (loc.id && loc.organizationId) {
          locationOrgMap.set(loc.id, loc.organizationId);
        }
      });
      
      // Also fetch any locations from oldLocationIds that might not be in current organization's locations list
      // This handles locations from other organizations
      const allLocationIds = new Set([...oldLocationIds, ...locationIds]);
      const locationRefs = Array.from(allLocationIds).map(id => doc(db, 'locations', id));
      const locationSnaps = await Promise.all(locationRefs.map(ref => getDoc(ref).catch(() => null)));
      locationSnaps.forEach((snap, index) => {
        if (snap?.exists()) {
          const locData = snap.data();
          const locId = Array.from(allLocationIds)[index];
          if (locId && locData.organizationId) {
            locationOrgMap.set(locId, locData.organizationId);
          }
        }
      });
      
      // Separate locations by organization:
      // 1. Keep locations from other organizations (not current organization)
      const locationsFromOtherOrgs = oldLocationIds.filter((locId: string) => {
        const orgId = locationOrgMap.get(locId);
        return orgId && orgId !== currentOrganization.id;
      });
      
      // 2. New locations for current organization (the ones being assigned)
      const newLocationIds = [...new Set([...locationsFromOtherOrgs, ...locationIds])];
      
      await updateDoc(userRef, {
        locationIds: newLocationIds,
        updatedAt: serverTimestamp(),
      });

      // Log audit entry for location changes (only for current organization)
      if (currentUser?.id && currentOrganization?.id) {
        // Get old locations for current organization only
        const oldLocationIdsForCurrentOrg = oldLocationIds.filter((locId: string) => {
          const orgId = locationOrgMap.get(locId);
          return orgId === currentOrganization.id;
        });
        
        // Calculate changes only for current organization
        const added = locationIds.filter((id: string) => !oldLocationIdsForCurrentOrg.includes(id));
        const removed = oldLocationIdsForCurrentOrg.filter((id: string) => !locationIds.includes(id));
        
        if (added.length > 0 || removed.length > 0) {
          const changes = [{
            field: 'locationIds',
            oldValue: oldLocationIds,
            newValue: locationIds,
          }];
          
          // Log separate entries for added and removed locations
          if (added.length > 0) {
            await auditLogService.log({
              userId: userForLocations.id,
              action: 'location_assigned',
              performedBy: currentUser.id,
              performedByName: currentUser.name || 'Unknown',
              performedByEmail: currentUser.email || 'Unknown',
              organizationId: currentOrganization.id,
              changes,
              metadata: {
                addedCount: added.length,
                addedLocationIds: added,
              },
            });
          }
          
          if (removed.length > 0) {
            await auditLogService.log({
              userId: userForLocations.id,
              action: 'location_removed',
              performedBy: currentUser.id,
              performedByName: currentUser.name || 'Unknown',
              performedByEmail: currentUser.email || 'Unknown',
              organizationId: currentOrganization.id,
              changes,
              metadata: {
                removedCount: removed.length,
                removedLocationIds: removed,
              },
            });
          }
        }
      }

      showNotification('Locations assigned successfully', 'success');
      setUserForLocations(null);
      await loadUsers();
    } catch (error: any) {
      console.error('Error assigning locations:', error);
      showNotification(`Error assigning locations: ${error.message}`, 'error');
    }
  };

  const handleCloseLocationsPanel = () => {
    setUserForLocations(null);
  };


  // Get unique locations for filter
  const uniqueLocations = Array.from(new Set(locations.map(l => l.id))).filter(Boolean);

  // Filter invitations - only show pending ones
  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');
  const acceptedInvitations = invitations.filter(inv => inv.status === 'accepted');

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesLocation = selectedLocation === 'All Locations' || 
      (user.locationIds && user.locationIds.includes(selectedLocation));
    const matchesSearch = !searchTerm ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesLocation && matchesSearch;
  });

  // Check if current user is admin for Enterprise features
  const currentUserIsAdmin = currentOrganization?.id 
    ? isAdminInOrganization(currentUser, currentOrganization.id)
    : false;

  return (
    <div className="p-4 max-w-[95vw] mx-auto">
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">User Management</h2>
          <div className="flex gap-2">
            {activeTab === 'users' && (
              <>
                <button
                  onClick={handleAddUserClick}
                  className="btn-primary px-4 py-2 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Invite User
                </button>
                <button
                  onClick={loadUsers}
                  className="btn-secondary px-4 py-2"
                >
                  Refresh
                </button>
              </>
            )}
          </div>
        </div>

        {/* Tabs - Enterprise Only */}
        {isEnterprise && currentUserIsAdmin && (
          <div className="mb-6 border-b border-gray-200">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('users')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Users
              </button>
              <button
                onClick={() => setActiveTab('custom-roles')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'custom-roles'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Custom Roles
              </button>
            </nav>
          </div>
        )}

        {/* Custom Roles Tab Content */}
        {activeTab === 'custom-roles' && isEnterprise && currentUserIsAdmin && currentOrganization?.id && (
          <CustomRoleManagement organizationId={currentOrganization.id} />
        )}

        {/* Users Tab Content */}
        {activeTab === 'users' && (
          <>

        {/* No Organization Message */}
        {!currentOrganization?.id && (
          <div className="text-center py-12 bg-yellow-50 border border-yellow-200 rounded-lg mb-6">
            <Building2 className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-yellow-800">No Organization Active</p>
            <p className="text-sm mt-1 text-yellow-700 mb-4">
              Please create or join an organization to manage users
            </p>
            <button
              onClick={() => window.location.href = '/organizations'}
              className="btn-primary px-4 py-2"
            >
              Go to Organizations
            </button>
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md"
          />
        </div>

        {/* Location Filter */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">Filter by Location</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedLocation('All Locations')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedLocation === 'All Locations'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Locations
            </button>
            {uniqueLocations.map(locationId => {
              const location = locations.find(l => l.id === locationId);
              return (
                <button
                  key={locationId}
                  onClick={() => setSelectedLocation(locationId!)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedLocation === locationId
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {location?.name || 'Unknown'}
                </button>
              );
            })}
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Showing {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
            {selectedLocation !== 'All Locations' && ` in selected location`}
          </p>
        </div>

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-2 text-orange-700">Pending invitations</h3>
            <p className="text-sm text-gray-600 mb-4">
              Invites are opened via a link. For staff usernames (no inbox), use{' '}
              <span className="font-medium">Copy invite link</span>, or{' '}
              <span className="font-medium">Create account</span> (admin) to finish setup without the employee
              opening the link — automated email is not sent to staff usernames.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg overflow-hidden">
              <table className="min-w-full table-auto border-collapse">
                <thead className="bg-orange-100">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-700 border border-orange-200">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-700 border border-orange-200">Email / username</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-700 border border-orange-200">Role</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-700 border border-orange-200">Status</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-orange-700 border border-orange-200">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvitations.map((invitation, index) => (
                    <tr key={invitation.id} className={`${index % 2 === 0 ? 'bg-orange-25' : 'bg-orange-50'} hover:bg-orange-100`}>
                      <td className="px-3 py-2 border border-orange-200">
                        <span className="text-sm font-medium text-gray-900">{invitation.name}</span>
                      </td>
                      <td className="px-3 py-2 border border-orange-200">
                        <span className="text-xs text-gray-700">
                          {formatLoginIdentifierForDisplay(invitation.email)}
                        </span>
                      </td>
                      <td className="px-3 py-2 border border-orange-200">
                        <div className="flex flex-wrap gap-1">
                          {normalizeRoles((invitation as any).roles || invitation.role).map((role, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                              {role}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 border border-orange-200">
                        <div className="flex items-center gap-1">
                          {invitation.status === 'pending' && <Clock className="w-3 h-3 text-orange-500" />}
                          {invitation.status === 'accepted' && <CheckCircle className="w-3 h-3 text-green-500" />}
                          {invitation.status === 'expired' && <AlertCircle className="w-3 h-3 text-red-500" />}
                          <span className="text-xs capitalize">{invitation.status}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 border border-orange-200">
                        <div className="flex justify-center gap-1">
                          {invitation.status === 'pending' && (
                            <>
                              <button
                                type="button"
                                onClick={() => copyInvitationLink(invitation.token)}
                                className="text-emerald-700 hover:text-emerald-900 text-xs px-2 py-1"
                                title="Copy invite link"
                              >
                                <Link2 className="w-4 h-4" />
                              </button>
                              {isSyntheticStaffAuthEmail(invitation.email) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCompleteStaffInvitationRow(invitation);
                                    setCompleteStaffPassword('');
                                    setCompleteStaffConfirm('');
                                    setShowCompleteStaffModal(true);
                                  }}
                                  className="text-violet-700 hover:text-violet-900 text-xs px-2 py-1"
                                  title="Create account now (admin)"
                                >
                                  <UserPlus className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleResendInvitation(invitation.id)}
                                className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1"
                                title={
                                  isSyntheticStaffAuthEmail(invitation.email)
                                    ? 'Refresh expiry (no email sent for staff usernames)'
                                    : 'Resend invitation email'
                                }
                              >
                                <Mail className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleCancelInvitation(invitation.id)}
                                className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                                title="Cancel Invitation"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Users Table */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading users...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                    Email / username
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                    <div className="flex items-center gap-1">
                      <span>Roles</span>
                      <button
                        onClick={() => setShowRoleHelp(true)}
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                        title="Learn about roles"
                      >
                        <HelpCircle className="w-3 h-3" />
                      </button>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Locations</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => (
                  <tr key={user.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                    <td className="px-3 py-2 border border-gray-200">
                      <div className="flex items-center gap-2">
                        {user.picture && (
                          <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                        )}
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">{user.name}</span>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedUserForAudit(user);
                            setShowAuditTrail(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-800 text-xs px-1.5 py-1"
                          title="View Audit Trail"
                        >
                          <History className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      <span className="text-xs text-gray-700">
                        {formatLoginIdentifierForDisplay(user.email)}
                      </span>
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      {(() => {
                        const organizationId = currentOrganization?.id;
                        if (!organizationId) {
                          // Fallback to legacy roles if no organization
                          const allRoles = normalizeRoles(user.roles || user.role);
                          const systemRoles = allRoles.filter(r => SYSTEM_ROLES.includes(r as any));
                          const ownerRoles = allRoles.filter(r => OWNER_ROLES.includes(r as any));
                          
                          return (
                            <div className="space-y-1">
                              {systemRoles.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {systemRoles.map((role, idx) => (
                                    <span key={idx} className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">
                                      {role}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {ownerRoles.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {ownerRoles.map((role, idx) => (
                                    <span key={idx} className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                                      {role}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {allRoles.length === 0 && (
                                <span className="text-xs text-gray-400 italic">No roles</span>
                              )}
                            </div>
                          );
                        }

                        // Use organization-specific roles
                        const systemRole = getUserSystemRoleForOrganization(user, organizationId);
                        const ownerRoles = getUserOwnerRolesForOrganization(user, organizationId);
                        
                        // Debug: Log role retrieval for this user
                        if (user.id === currentUser?.id || ownerRoles.length === 0) {
                          const orgRolesForCurrentOrg = (user as any).organizationRoles?.[organizationId];
                          console.log('[UserManagement] Role Display Debug:', {
                            userId: user.id,
                            userName: user.name,
                            organizationId: organizationId,
                            organizationName: currentOrganization?.name,
                            userOrganizationRoles: (user as any).organizationRoles,
                            currentOrgRoles: orgRolesForCurrentOrg,
                            systemRole: systemRole,
                            ownerRoles: ownerRoles,
                            ownerRolesCount: ownerRoles.length,
                            availableOwnerRoles: Array.from(availableOwnerRoles)
                          });
                        }
                        
                        return (
                          <div className="space-y-1">
                            {/* System Role */}
                            {systemRole && (
                              <div className="flex flex-wrap gap-1">
                                <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">
                                  {systemRole}
                                </span>
                              </div>
                            )}
                            {/* Owner Roles */}
                            {ownerRoles.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {ownerRoles.map((role, idx) => (
                                  <span key={idx} className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                                    {role}
                                  </span>
                                ))}
                              </div>
                            )}
                            {!systemRole && ownerRoles.length === 0 && (
                              <span className="text-xs text-gray-400 italic">No roles</span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      <div className="flex flex-wrap gap-1">
                        {user.locationIds && user.locationIds.length > 0 ? (
                          locations
                            .filter(l => user.locationIds?.includes(l.id!))
                            .map(location => (
                              <span key={location.id} className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                                {location.name}
                              </span>
                            ))
                        ) : (
                          <span className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded font-medium">⚠️ No locations assigned</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      <div className="flex justify-center gap-1">
                        {/* Show edit button only if user can edit this user */}
                        {(() => {
                          const organizationId = currentOrganization?.id;
                          if (!organizationId) return null;

                          const userSystemRole = getUserSystemRoleForOrganization(user, organizationId);
                          const hasSystemAdmin = userSystemRole === 'admin';
                          const canEdit = !(hasSystemAdmin && currentUser?.id !== user.id) && 
                                         !(userSystemRole === 'user' && currentUser?.id === user.id);
                          return canEdit && (
                            <button
                              onClick={() => handleOpenEditModal(user)}
                              className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1"
                              title="Edit User"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          );
                        })()}

                        {canSetStaffMemberPassword(user) && (
                          <button
                            type="button"
                            onClick={() => {
                              setSetPasswordForUser(user);
                              setSetPasswordNew('');
                              setSetPasswordConfirm('');
                              setShowSetPasswordModal(true);
                            }}
                            className="text-amber-700 hover:text-amber-900 text-xs px-2 py-1"
                            title="Set password (staff username — no email recovery)"
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                        )}
                        
                        {/* Show assign locations button only if user can assign locations */}
                        {(() => {
                          const organizationId = currentOrganization?.id;
                          if (!organizationId) return null;

                          const userSystemRole = getUserSystemRoleForOrganization(user, organizationId);
                          const hasSystemAdmin = userSystemRole === 'admin';
                          const currentUserHasSystemAdmin = isAdminInOrganization(currentUser, organizationId);
                          const canAssign = !(hasSystemAdmin && !currentUserHasSystemAdmin) && 
                                          !(userSystemRole === 'user' && currentUser?.id === user.id);
                          return canAssign && (
                            <button
                              onClick={() => handleAssignLocations(user)}
                              className="text-green-600 hover:text-green-800 text-xs px-2 py-1"
                              title="Assign Locations"
                            >
                              <MapPin className="w-4 h-4" />
                            </button>
                          );
                        })()}
                        
                        {/* Show remove from organization or delete button */}
                        {(() => {
                          const organizationId = currentOrganization?.id;
                          if (!organizationId) return null;

                          const isCurrentUser = user.id === currentUser?.id;
                          
                          // Check if user can be removed/deleted based on role hierarchy
                          const canModify = canRemoveUser(currentUser, user, organizationId, isCurrentUser);
                          
                          if (!canModify) return null;

                          // Check if user belongs to multiple organizations
                          const userOrgIds = (user as any).organizationIds || [];
                          // Backward compatibility: if user has organizationId but it's not in organizationIds array, count it
                          let totalOrgCount = userOrgIds.length;
                          if (user.organizationId && !userOrgIds.includes(user.organizationId)) {
                            totalOrgCount += 1;
                          }
                          const hasMultipleOrgs = totalOrgCount > 1;

                          // Show "Remove from Organization" if user belongs to multiple orgs, otherwise show "Delete User"
                          if (hasMultipleOrgs) {
                            return (
                              <button
                                onClick={() => user.id && handleRemoveFromOrganization(user.id)}
                                className="text-orange-600 hover:text-orange-800 text-xs px-2 py-1"
                                title="Remove from Organization"
                              >
                                <UserMinus className="w-4 h-4" />
                              </button>
                            );
                          } else {
                            return (
                              <button
                                onClick={() => user.id && handleDeleteUser(user.id)}
                                className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                                title="Delete User (removes from all organizations)"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            );
                          }
                        })()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredUsers.length === 0 && !isLoading && (
          <div className="text-center py-8 text-gray-500 border border-gray-200 rounded">
            <p className="text-lg font-medium">No users found</p>
            <p className="text-sm mt-1">{searchTerm && 'Try a different search term or clear filters.'}</p>
          </div>
        )}

          {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-800">Invite New User</h3>
              <button 
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({ name: '', email: '', roles: ['user'], selectedLocationIds: [] });
                }} 
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email or username *
                </label>
                <input
                  type="text"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g. marina-housekeeping or name@company.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Staff without an inbox: use a unique username for your property, e.g.{' '}
                  <span className="font-mono text-gray-700">yourpropertycode-housekeeping</span>. Usernames are
                  unique across all properties.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <span>Roles *</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowRoleHelp(true);
                    }}
                    className="text-blue-500 hover:text-blue-700 transition-colors"
                    title="Learn about roles"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </label>
                <div className="border border-gray-300 rounded-md p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* System Roles - Only one can be selected */}
                    <div>
                      <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">System Role *</p>
                      <p className="text-xs text-gray-500 mb-2">Select one system role</p>
                      <div className="space-y-1.5">
                        {SYSTEM_ROLES.map((role) => {
                          const organizationId = currentOrganization?.id;
                          const canSelectAdmin = role !== 'admin' || (organizationId && isAdminInOrganization(currentUser, organizationId));
                          const isDisabled = !canSelectAdmin || !organizationId;
                          const currentSystemRole = formData.roles.find(r => SYSTEM_ROLES.includes(r as any));
                          const isSelected = currentSystemRole === role;
                          
                          return (
                            <label
                              key={role}
                              className={`flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 ${
                                isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                              }`}
                            >
                              <input
                                type="radio"
                                name="systemRole"
                                value={role}
                                checked={isSelected}
                                onChange={(e) => {
                                  if (isDisabled) return;
                                  // Remove all system roles and add the selected one
                                  const ownerRoles = formData.roles.filter(r => OWNER_ROLES.includes(r as any));
                                  setFormData({ ...formData, roles: [role, ...ownerRoles] });
                                }}
                                disabled={isDisabled}
                                className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700 capitalize">{role}</span>
                              {isDisabled && (
                                <span className="text-xs text-gray-500 ml-auto">(Admin only)</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Owner Roles */}
                    <div>
                      <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Owner Roles</p>
                      <div className="grid grid-cols-1 gap-1.5">
                        {availableOwnerRoles.map((role) => (
                          <label
                            key={role}
                            className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer"
                          >
                            {formData.roles.includes(role) ? (
                              <CheckSquare className="w-4 h-4 text-primary-600 flex-shrink-0" />
                            ) : (
                              <Square className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                            <span className="text-sm text-gray-700">{role}</span>
                            <input
                              type="checkbox"
                              checked={formData.roles.includes(role)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, roles: [...formData.roles, role] });
                                } else {
                                  setFormData({ ...formData, roles: formData.roles.filter(r => r !== role) });
                                }
                              }}
                              className="hidden"
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Select one or more roles for this user</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Locations *
                </label>
                <p className="text-xs text-gray-500 mb-3">Select at least one location to assign to this user</p>
                {locations.length === 0 ? (
                  <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                    <p className="text-sm text-gray-600">No locations available. Please create locations first.</p>
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded-md p-4 max-h-64 overflow-y-auto">
                    <div className="space-y-2">
                      {/* Select All Option */}
                      <label className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer border-b border-gray-200 pb-3">
                        <input
                          type="checkbox"
                          checked={formData.selectedLocationIds.length === locations.length && locations.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const allLocationIds = locations.map(l => l.id).filter(Boolean) as string[];
                              setFormData({ ...formData, selectedLocationIds: allLocationIds });
                            } else {
                              setFormData({ ...formData, selectedLocationIds: [] });
                            }
                          }}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm font-medium text-gray-900">
                          Select All ({locations.length} locations)
                        </span>
                      </label>
                      
                      {/* Location List */}
                      {locations.map(location => (
                        <label
                          key={location.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={location.id ? formData.selectedLocationIds.includes(location.id) : false}
                            onChange={(e) => {
                              if (!location.id) return;
                              if (e.target.checked) {
                                setFormData({ 
                                  ...formData, 
                                  selectedLocationIds: [...formData.selectedLocationIds, location.id] 
                                });
                              } else {
                                setFormData({ 
                                  ...formData, 
                                  selectedLocationIds: formData.selectedLocationIds.filter(id => id !== location.id) 
                                });
                              }
                            }}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <div className="flex-1">
                            <span className="text-sm text-gray-900 font-medium">
                              {location.name}
                            </span>
                            <div className="flex gap-2 mt-0.5">
                              {location.state && (
                                <>
                                  <span className="text-xs text-gray-500">{location.state}</span>
                                  {location.country && <span className="text-xs text-gray-500">•</span>}
                                </>
                              )}
                              {location.country && (
                                <span className="text-xs text-gray-500">{location.country}</span>
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {formData.selectedLocationIds.length === 0 && (
                  <p className="text-xs text-red-600 mt-1">Please select at least one location</p>
                )}
                {formData.selectedLocationIds.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.selectedLocationIds.length} location{formData.selectedLocationIds.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({ name: '', email: '', roles: ['user'], selectedLocationIds: [] });
                }}
                className="btn-secondary px-5 py-2"
              >
                Cancel
              </button>
                <button
                  type="submit"
                  className="btn-primary px-5 py-2"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending Invitation...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-800">Edit User</h3>
              <button onClick={handleCloseEditModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleEditUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email or username *
                </label>
                <input
                  type="text"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g. marina-housekeeping or name@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <span>Roles *</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowRoleHelp(true);
                    }}
                    className="text-blue-500 hover:text-blue-700 transition-colors"
                    title="Learn about roles"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </label>
                <div className="border border-gray-300 rounded-md p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* System Roles - Only one can be selected */}
                    <div>
                      <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">System Role *</p>
                      <p className="text-xs text-gray-500 mb-2">Select one system role</p>
                      <div className="space-y-1.5">
                        {SYSTEM_ROLES.map((role) => {
                          const organizationId = currentOrganization?.id;
                          if (!organizationId) return null;
                          
                          const editingUserSystemRole = getUserSystemRoleForOrganization(editingUser, organizationId);
                          const isEditingSystemAdmin = editingUserSystemRole === 'admin';
                          const isSelfEdit = currentUser?.id === editingUser?.id;
                          const currentUserHasSystemAdmin = isAdminInOrganization(currentUser, organizationId);
                          const currentUserSystemRole = getUserSystemRoleForOrganization(currentUser, organizationId);
                          const currentUserIsManager = currentUserSystemRole === 'manager'; // Only true for managers, not admins
                          const currentUserIsUser = currentUserSystemRole === 'user';
                          
                          // Disable admin radio if:
                          // - Editing another admin (unless it's yourself)
                          // - Current user is manager and trying to assign admin
                          // - Current user is regular user
                          const canModifyAdmin = role !== 'admin' || 
                            (isEditingSystemAdmin && isSelfEdit) || 
                            (currentUserHasSystemAdmin && !isEditingSystemAdmin) ||
                            (!isEditingSystemAdmin && currentUserIsManager);
                          const isDisabled = !canModifyAdmin || currentUserIsUser;
                          
                          const currentSystemRole = formData.roles.find(r => SYSTEM_ROLES.includes(r as any));
                          const isSelected = currentSystemRole === role;
                          
                          return (
                            <label
                              key={role}
                              className={`flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 ${
                                isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                              }`}
                            >
                              <input
                                type="radio"
                                name="systemRole"
                                value={role}
                                checked={isSelected}
                                onChange={(e) => {
                                  if (isDisabled) return;
                                  // Prevent changing admin role if user is admin and editing themselves
                                  if (role !== 'admin' && isEditingSystemAdmin && isSelfEdit) {
                                    showNotification('Admin users cannot change their own admin role.', 'error');
                                    return;
                                  }
                                  // Remove all system roles and add the selected one, keep owner roles
                                  const ownerRoles = formData.roles.filter(r => OWNER_ROLES.includes(r as any));
                                  setFormData({ ...formData, roles: [role, ...ownerRoles] });
                                }}
                                disabled={isDisabled}
                                className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700 capitalize">{role}</span>
                              {isDisabled && role === 'admin' && (
                                <span className="text-xs text-gray-500 ml-auto">(Restricted)</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Owner Roles */}
                    <div>
                      <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Owner Roles</p>
                      <div className="grid grid-cols-1 gap-1.5">
                        {availableOwnerRoles.map((role) => (
                          <label
                            key={role}
                            className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer"
                          >
                            {formData.roles.includes(role) ? (
                              <CheckSquare className="w-4 h-4 text-primary-600 flex-shrink-0" />
                            ) : (
                              <Square className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                            <span className="text-sm text-gray-700">{role}</span>
                            <input
                              type="checkbox"
                              checked={formData.roles.includes(role)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, roles: [...formData.roles, role] });
                                } else {
                                  setFormData({ ...formData, roles: formData.roles.filter(r => r !== role) });
                                }
                              }}
                              className="hidden"
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Select one system role (User, Admin, or Manager) and optionally add owner roles</p>
                {(() => {
                  const organizationId = currentOrganization?.id;
                  if (!organizationId) return null;

                  const editingUserSystemRole = getUserSystemRoleForOrganization(editingUser, organizationId);
                  const hasSystemAdmin = editingUserSystemRole === 'admin';
                  const currentUserSystemRole = getUserSystemRoleForOrganization(currentUser, organizationId);
                  const currentUserIsManager = currentUserSystemRole === 'manager'; // Only true for managers, not admins
                  const currentUserIsAdmin = currentUserSystemRole === 'admin';
                  return (
                    <>
                      {hasSystemAdmin && currentUser?.id !== editingUser.id && !currentUserIsAdmin && (
                        <p className="text-xs text-red-600 mt-1">You cannot change the roles of admin users</p>
                      )}
                      {currentUserIsManager && hasSystemAdmin && (
                        <p className="text-xs text-red-600 mt-1">Managers cannot modify admin users</p>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Custom Role Assignment - Enterprise Only */}
              {isEnterprise && currentOrganization?.id && editingUser && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Custom Roles</h3>
                  <CustomRoleAssignment
                    userId={editingUser.id}
                    organizationId={currentOrganization.id}
                    onUpdate={loadUsers}
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="btn-secondary px-5 py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary px-5 py-2"
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
          </>
        )}

      {/* Assign Locations Panel */}
      {userForLocations && (
        <AssignLocationsPanel
          user={userForLocations}
          locations={locations}
          users={users}
          onAssign={handleSaveLocationAssignment}
          onClose={handleCloseLocationsPanel}
        />
      )}

      {/* Role Help Modal */}
      <RoleHelpModal
        isOpen={showRoleHelp}
        onClose={() => setShowRoleHelp(false)}
      />

      {/* User Audit Trail Modal */}
      {showAuditTrail && selectedUserForAudit && (
        <UserAuditTrail
          userId={selectedUserForAudit.id}
          userName={selectedUserForAudit.name || 'Unknown'}
          userEmail={selectedUserForAudit.email}
          onClose={() => {
            setShowAuditTrail(false);
            setSelectedUserForAudit(null);
          }}
        />
      )}

      {showSetPasswordModal && setPasswordForUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <form onSubmit={handleSubmitSetPassword}>
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">Set password</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowSetPasswordModal(false);
                    setSetPasswordForUser(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600">
                  Set a new sign-in password for{' '}
                  <span className="font-mono font-medium text-gray-900">
                    {formatLoginIdentifierForDisplay(setPasswordForUser.email)}
                  </span>
                  . Staff usernames cannot use email reset — share this password with them in person or over a
                  trusted channel.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={setPasswordNew}
                    onChange={(e) => setSetPasswordNew(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                    placeholder="At least 6 characters"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={setPasswordConfirm}
                    onChange={(e) => setSetPasswordConfirm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div className="px-6 pb-6 flex justify-end gap-3">
                <button
                  type="button"
                  className="btn-secondary px-5 py-2"
                  onClick={() => {
                    setShowSetPasswordModal(false);
                    setSetPasswordForUser(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary px-5 py-2" disabled={setPasswordLoading}>
                  {setPasswordLoading ? 'Saving…' : 'Save password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCompleteStaffModal && completeStaffInvitationRow && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <form onSubmit={handleSubmitCompleteStaffInvitation}>
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">Create account (staff username)</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowCompleteStaffModal(false);
                    setCompleteStaffInvitationRow(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600">
                  Creates the login for{' '}
                  <span className="font-mono font-medium text-gray-900">
                    {formatLoginIdentifierForDisplay(completeStaffInvitationRow.email)}
                  </span>{' '}
                  without them opening the invite link. Share the password with them securely.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Temporary password (optional)
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={completeStaffPassword}
                    onChange={(e) => setCompleteStaffPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                    placeholder="Leave blank to auto-generate"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    At least 6 characters if you set one. Leave blank for a one-time generated password.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={completeStaffConfirm}
                    onChange={(e) => setCompleteStaffConfirm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div className="px-6 pb-6 flex justify-end gap-3">
                <button
                  type="button"
                  className="btn-secondary px-5 py-2"
                  onClick={() => {
                    setShowCompleteStaffModal(false);
                    setCompleteStaffInvitationRow(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary px-5 py-2" disabled={completeStaffLoading}>
                  {completeStaffLoading ? 'Working…' : 'Create account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default UserManagement;

